import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import type {
	ActivationIdsGraphQLResponse,
	ChartLinkPayload,
	WorkspaceToWorkspaceIdRecord,
} from './types';

export const ACTIVATION_IDS_QUERY = `
  query ActivationIds($cloudId: ID!) {
    tenantContexts(cloudIds: [$cloudId]) {
      activationIds {
        product
        active
      }
    }
  }
`;

const ENV_VAR_FRAGMENT = `
  fragment envVarFields on AVPEnvVar {
    id
    name
    chartId
    chartType
    dataType
    defaultValues
    hotTierFilterConfig {
      datasourceLocator {
        dstype
        workspaceId
      }
      product
      semanticModel
      dimension
    }
    operator
    metadata
  }
`;

const PIPELINE_FRAGMENT = `
  fragment pipelineFields on AVPChartPipeline {
    id
    queries {
      id
      datasourceLocator {
        dstype
        workspaceId
      }
      metricsConfiguration {
        metrics
        dimensions {
          type
          name
        }
        filters {
          product
          dimension
          comparison
          operands
        }
      }
    }
  }
`;

const CHART_FRAGMENT = `
  fragment chartFields on AVPChart {
    id
    chartType
    chartLayout {
      x
      y
      z
      blocksWide
      blocksHigh
    }
    envVar {
      ...envVarFields
    }
    templateChartId
    chartConfig {
      clientSettings {
        type
        options {
          name
          jsonValue
        }
      }
      settings {
        name
        jsonValue
      }
    }
  }
  ${ENV_VAR_FRAGMENT}
`;

const CHART_FRAGMENT_WITH_PIPELINE = `
  fragment chartFieldsWithPipeline on AVPChart {
    ...chartFields
    pipeline {
      ...pipelineFields
    }
  }
  ${CHART_FRAGMENT}
  ${PIPELINE_FRAGMENT}
`;

const CANVAS_LAYOUT_FRAGMENT = `
  fragment canvasLayoutFields on AVPDashboardCanvasLayout {
    version
    rows {
      id
      height
      columns: elements {
        id
        span
        item {
          id
          template_chart_id: templateChartId
        }
      }
    }
    controlIds
  }
`;

const MUTATION_PAYLOAD_FRAGMENT = `
  fragment payloadFields on Payload {
    success
    errors {
      message
      extensions {
        statusCode
        errorType
      }
    }
  }
`;

export const CREATE_STANDALONE_CHART_QUERY = `
mutation avp_createStandaloneChart($chart: AVPChartInput!, $cloudId: ID!, $containerAri: ID, $workspaceAri: ID, $integrationId: AVPIntegrationId) {
  avp_createStandaloneChart(
    input: {chart: $chart, cloudId: $cloudId, containerAri: $containerAri, workspaceAri: $workspaceAri, integrationId: $integrationId}
  ) @optIn(to: "AVP-standalone-charts") {
    success
    avpVizUrl
    chart {
      id
      chartType
      pipeline {
        queries {
          metricsConfiguration {
            metrics
            dimensions {
              type
              name
            }
            filters {
              product
              dimension
              comparison
              operands
            }
            workspaceId
            searchCondition
          }
        }
        nodes
      }
      chartConfig {
        creator
        createdAt
        updatedAt
        settings {
          name
          jsonValue
        }
        clientSettings {
          type
          options {
            name
            jsonValue
          }
        }
		dataFetchStrategy
        vizConfig {
		  accessors
          data
        }
      }
    }
    dashboard {
      id
      isReadOnly
      access
    }
    errors {
      message
      extensions {
        statusCode
        errorType
      }
    }
  }
}
`;

export const CREATE_CHART_TO_DASHBOARD_MUTATION: '\n  mutation avp_createChart($dashboardAri: ID!, $chart: AVPChartInput!, $canvasElementId: ID) {\n    avp_createChart(input: { dashboardAri: $dashboardAri, chart: $chart, canvasElementId: $canvasElementId }) @optIn(to: "AVP-standalone-charts") {\n      chart {\n        ...chartFieldsWithPipeline\n      }\n      canvasLayout {\n        ...canvasLayoutFields\n      }\n      ...payloadFields\n    }\n  }\n\n  \n  fragment chartFieldsWithPipeline on AVPChart {\n    ...chartFields\n    pipeline {\n      ...pipelineFields\n    }\n  }\n  \n  fragment chartFields on AVPChart {\n    id\n    chartType\n    chartLayout {\n      x\n      y\n      z\n      blocksWide\n      blocksHigh\n    }\n    envVar {\n      ...envVarFields\n    }\n    templateChartId\n    chartConfig {\n      clientSettings {\n        type\n        options {\n          name\n          jsonValue\n        }\n      }\n      settings {\n        name\n        jsonValue\n      }\n    }\n  }\n  \n  fragment envVarFields on AVPEnvVar {\n    id\n    name\n    chartId\n    chartType\n    dataType\n    defaultValues\n    hotTierFilterConfig {\n      datasourceLocator {\n        dstype\n        workspaceId\n      }\n      product\n      semanticModel\n      dimension\n    }\n    operator\n    metadata\n  }\n\n\n  \n  fragment pipelineFields on AVPChartPipeline {\n    id\n    queries {\n      id\n      datasourceLocator {\n        dstype\n        workspaceId\n      }\n      metricsConfiguration {\n        metrics\n        dimensions {\n          type\n          name\n        }\n        filters {\n          product\n          dimension\n          comparison\n          operands\n        }\n      }\n    }\n  }\n\n\n  \n  fragment canvasLayoutFields on AVPDashboardCanvasLayout {\n    version\n    rows {\n      id\n      height\n      columns: elements {\n        id\n        span\n        item {\n          id\n          template_chart_id: templateChartId\n        }\n      }\n    }\n    controlIds\n  }\n\n  \n  fragment payloadFields on Payload {\n    success\n    errors {\n      message\n      extensions {\n        statusCode\n        errorType\n      }\n    }\n  }\n\n' = `
  mutation avp_createChart($dashboardAri: ID!, $chart: AVPChartInput!, $canvasElementId: ID) {
    avp_createChart(input: { dashboardAri: $dashboardAri, chart: $chart, canvasElementId: $canvasElementId }) @optIn(to: "AVP-standalone-charts") {
      chart {
        ...chartFieldsWithPipeline
      }
      canvasLayout {
        ...canvasLayoutFields
      }
      ...payloadFields
    }
  }

  ${CHART_FRAGMENT_WITH_PIPELINE}
  ${CANVAS_LAYOUT_FRAGMENT}
  ${MUTATION_PAYLOAD_FRAGMENT}
`;

function mapChartTypeToGraphQL(chartType: string): string {
	const chartTypeMap: Record<string, string> = {
		bar: 'bar',
		horizontalbar: 'bar_horizontal',
		stackbar: 'bar_stack',
		horizontalstackbar: 'bar_stack_horizontal',
		line: 'line',
		pie: 'pie',
		donut: 'donut',
	};

	return chartTypeMap[chartType] || 'bar';
}

/**
 * Extracts product name from metric string (e.g., "jira/issue_count" -> "jira")
 */
export function extractProductFromMetric(metric: string): string | null {
	if (!metric || !metric.includes('/')) {
		return null;
	}
	const parts = metric.split('/');
	return parts.length > 0 && parts[0] ? parts[0] : null;
}

/**
 * Get activation IDs for a specific cloud ID using GraphQL
 * This maps products to their workspace IDs (activation IDs)
 * Adapted from avp/packages/product-metrics-provider/src/store/store.ts
 * @param performGqlFetch - GraphQL fetch function to use (allows different implementations for mocks vs production)
 */
// The activation-id mapping is stable for a given cloudId, so cache the
// in-flight promise per cloudId. This dedupes concurrent callers (e.g. the
// export picker and metric workspace resolution) and avoids repeated network
// calls. Failed lookups are evicted so they can be retried.
const activationIdMappingCache = new Map<string, Promise<WorkspaceToWorkspaceIdRecord>>();

export async function getActivationIdMappingByCloudId(
	cloudId: string,
): Promise<WorkspaceToWorkspaceIdRecord> {
	const cached = activationIdMappingCache.get(cloudId);
	if (cached) {
		return cached;
	}

	const request = (async (): Promise<WorkspaceToWorkspaceIdRecord> => {
		try {
			const response = await performGqlFetch<ActivationIdsGraphQLResponse, { cloudId: string }>({
				gqlQuery: ACTIVATION_IDS_QUERY,
				variables: { cloudId },
			});

			const tenantContext = response.data?.tenantContexts?.[0];
			if (!tenantContext) {
				throw new Error(`No tenant context found for cloud ID: ${cloudId}`);
			}

			return tenantContext.activationIds.reduce((acc, activationId) => {
				acc[activationId.product] = activationId.active;
				return acc;
			}, {} as WorkspaceToWorkspaceIdRecord);
		} catch (error) {
			throw new Error(
				`Failed to get activation ID mapping for cloud ID: ${cloudId}. ${error instanceof Error ? error.message : String(error)}`,
			);
		}
	})();

	activationIdMappingCache.set(cloudId, request);
	request.catch(() => activationIdMappingCache.delete(cloudId));
	return request;
}

export function resolveWorkspaceIdForMetricWithMapping(
	activationIdMap: WorkspaceToWorkspaceIdRecord,
	metric: string,
): string | undefined {
	const product = extractProductFromMetric(metric);
	if (!product) {
		throw new Error(`Failed to extract product from metric: ${metric}`);
	}
	return activationIdMap[product];
}

/**
 * Resolves workspaceId for datasourceLocator based on the product from metric
 * Uses activation ID mapping to get the workspaceId for the product
 */
export async function resolveWorkspaceIdForMetric(
	cloudId: string,
	metric: string,
): Promise<string | undefined> {
	const product = extractProductFromMetric(metric);
	if (!product) {
		throw new Error(`Failed to extract product from metric: ${metric}`);
	}

	try {
		const activationIdMap = await getActivationIdMappingByCloudId(cloudId);
		return activationIdMap[product];
	} catch (error) {
		throw new Error(
			`Failed to resolve workspaceId for metric: ${metric}. ${error instanceof Error ? error.message : String(error)}`,
		);
	}
}

/**
 * Transforms dimensions from { name, dynamicDimensionKey } to { name }
 */
type RawRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RawRecord {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function transformDimensions(dimensions: unknown[]): Array<{ name: string }> {
	if (!dimensions || !Array.isArray(dimensions)) {
		return [];
	}
	return dimensions.map((dim) => {
		const name = isRecord(dim) ? dim.name : dim;
		return { name: String(name), type: '' }; // TODO: DXAI-1066
	});
}

/**
 * Transform filters: extract dimension.name from nested dimension object
 */
function transformFilters(
	filters: unknown[],
): Array<{ product: string; dimension: string; comparison: string; operands: string[] }> {
	if (!filters || !Array.isArray(filters)) {
		return [];
	}
	return filters.map((filter) => {
		if (!isRecord(filter)) {
			return { product: '', dimension: '', comparison: '', operands: [] };
		}
		const rawDimension = filter.dimension;
		const dimensionName = isRecord(rawDimension) ? rawDimension.name : rawDimension;
		return {
			product: typeof filter.product === 'string' ? filter.product : '',
			dimension: String(dimensionName ?? ''),
			comparison: typeof filter.comparison === 'string' ? filter.comparison : '',
			operands: Array.isArray(filter.operands)
				? filter.operands.filter((o): o is string => typeof o === 'string')
				: [],
		};
	});
}

/**
 * Transforms chart data into a 2D array format.
 * @param payload - The chart payload with data and accessors
 * @returns Object with data (2D array format) and accessors
 */
function transformChartData(payload: ChartLinkPayload): {
	data: Array<Array<string | number>>;
	accessors: Record<string, string | number>;
} {
	if (!payload.data || !Array.isArray(payload.data) || payload.data.length === 0) {
		return { data: [], accessors: {} };
	}

	const { xAccessor, yAccessor, labelAccessor, valueAccessor, colorAccessor } = payload;

	if (xAccessor !== undefined && yAccessor !== undefined) {
		const data = payload.data.map((item) => {
			const record = isRecord(item) ? item : {};
			const row: Array<string | number> = [
				isNaN(Number(record[xAccessor])) ? String(record[xAccessor]) : Number(record[xAccessor]),
				isNaN(Number(record[yAccessor])) ? String(record[yAccessor]) : Number(record[yAccessor]),
			];
			if (colorAccessor !== undefined && record[colorAccessor] !== undefined) {
				row.push(String(record[colorAccessor]));
			}
			return row;
		});
		return {
			data,
			accessors: {
				x: 0,
				y: 1,
				...(colorAccessor !== undefined ? { color: 2 } : {}),
			},
		};
	}

	if (labelAccessor !== undefined && valueAccessor !== undefined) {
		const data = payload.data.map((item) => {
			const record = isRecord(item) ? item : {};
			const row: Array<string | number> = [
				String(record[labelAccessor]),
				isNaN(Number(record[valueAccessor]))
					? String(record[valueAccessor])
					: Number(record[valueAccessor]),
			];
			if (colorAccessor !== undefined && record[colorAccessor] !== undefined) {
				row.push(String(record[colorAccessor]));
			}
			return row;
		});
		return {
			data,
			accessors: {
				label: 0,
				value: 1,
				...(colorAccessor !== undefined ? { color: 2 } : {}),
			},
		};
	}

	return { data: [], accessors: {} };
}

/**
 * Builds chart config options from payload
 */
function buildChartConfigOptions(
	payload: ChartLinkPayload,
): Array<{ name: string; jsonValue: string }> {
	const options: Array<{ name: string; jsonValue: string }> = [];
	if (payload.chartTitle) {
		options.push({
			name: 'title',
			jsonValue: JSON.stringify(payload.chartTitle),
		});
	}
	if (payload.subtitle) {
		options.push({
			name: 'subtitle',
			jsonValue: JSON.stringify(payload.subtitle),
		});
	}
	return options;
}

/**
 * Transforms VisualizationChartProps into the AVPChartInput format required for the GraphQL mutation
 * @param payload - The VisualizationChartProps to transform
 * @param workspaceId - The workspaceId for the metric
 */
export function transformChartPayloadToAvpInput(
	payload: ChartLinkPayload,
	workspaceId?: string,
	chartId?: string,
	dataFetchStrategy?: 'STATIC' | 'DYNAMIC',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
	const avpChartType = mapChartTypeToGraphQL(payload.chartType);
	const chartConfigOptions = buildChartConfigOptions(payload);
	const { data, accessors } = transformChartData(payload);
	const metricsConfig = payload.metricsConfig;
	const metrics = metricsConfig?.metricNames;
	const metricWorkspace = workspaceId || metricsConfig?.workspaceId;

	if (metrics && metrics.length > 0 && !metricWorkspace) {
		throw new Error('No workspaceId found for the metricConfig and datasourceLocator');
	}

	if (data.length === 0) {
		throw new Error('Cannot create chart: chart requires non-empty data and resolvable accessors');
	}

	const resolvedDataFetchStrategy =
		dataFetchStrategy ?? ((metrics?.length ?? 0) > 0 ? 'DYNAMIC' : 'STATIC');

	return {
		chartType: 'avp_chart',
		...(chartId ? { id: chartId } : {}),
		chartConfig: {
			settings: [],
			clientSettings: {
				type: avpChartType,
				options: chartConfigOptions,
			},
			dataFetchStrategy: resolvedDataFetchStrategy,
			vizConfig: {
				accessors,
				data,
			},
		},
		...(metrics && metrics.length > 0 && metricsConfig
			? {
					pipeline: {
						nodes: [],
						queries: [
							{
								isManual: false,
								metricsConfiguration: {
									metrics,
									dimensions: transformDimensions(metricsConfig.dimensions ?? []),
									filters: transformFilters(metricsConfig.filters ?? []),
									granularity: metricsConfig.granularity || null,
									workspaceId: metricWorkspace,
									searchCondition: metricsConfig.searchCondition || null,
								},
								datasourceLocator: {
									dstype: 'HotTier',
									workspaceId: metricWorkspace,
								},
							},
						],
					},
				}
			: {}),
	};
}
